import {CreateAccessor, observer_mgl} from "mobx-graphlink";
import React, {useState} from "react";
import {ES, hourInMS, RunInAction_Set} from "web-vcore";
import {GetEntities_WithUserTag} from "../../../../Server/DB/entities.js";
import {store} from "../../../../Store/index.js";
import {OverlayPanel} from "../../../../Store/main/tools/journey.js";
import {GetJournalEntries, Journey_GetSessionPeriodsToShow} from "../../../../Server/DB/journalEntries.js";
import {JournalSegment} from "../../../../Server/DB/journalEntries/@JournalEntry.js";
import {GetDreamsToShow_EntryList} from "../../../Journal/DreamList.js";
import {MeID} from "../../../../Server/DB/users.js";
import {JournalSort} from "../../../../Store/main/journal.js";
import {GetAllLetterAnchorEntities, GetDreamAnchorEntities, GetDreamTitles, GetLastXDreamScenesPerLetterAnchor, GetStandardDeepOrderingKeyForDreamScene, dreamCellLetters} from "../../../../Server/DB/journalEntries/$journalEntry.js";
import {Assert, E} from "js-vextensions";
import {Entity} from "../../../../Server/DB/entities/@Entity.js";
import {EstimateSegmentStartTime} from "../../../../Server/DB/@Shared/JournalTimeEstimates.js";

const GRID_COLS = 4;
const GRID_ROWS = 7;
const GRID_TOTAL = GRID_COLS * GRID_ROWS; // 28
const GRID_TOTAL_DREAMS = GRID_TOTAL - 2; // 26; last 2 cells are "control cells" (ie. for ui/buttons)
const cellW = `${100 / GRID_COLS}vw`;
const cellH = `${100 / GRID_ROWS}vh`;

const cellIndex_close = GRID_TOTAL - 4; // 4th-last cell (bottom-left)
const cellIndex_toggle = GRID_TOTAL - 1; // last cell (bottom-right)
const controlCellIndexes = [cellIndex_close, cellIndex_toggle];
const getDreamCellIndex = (cellIndex: number)=>{
	Assert(!controlCellIndexes.includes(cellIndex), "getDreamCellIndex called with control-cell index!");
	return cellIndex - controlCellIndexes.filter(a=>a < cellIndex).length;
}

export const RecallGridOverlay = observer_mgl(()=>{
	const uiState = store.main.engine.journey;
	const [showTitles, setShowTitles] = useState(false);

	return (
		<div style={{
			position: "fixed", left: 0, top: 0, right: 0, bottom: 0, zIndex: 100,
			display: "grid",
			gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
			gridTemplateRows: `repeat(${GRID_ROWS}, 1fr)`,
			background: "black",
		}}>
			{Array.from({length: GRID_TOTAL}, (_, i)=>{
				//const isControlCell = controlCellIndexes.includes(i);
				if (i == cellIndex_close) {
					return (
						<div key={i}
							style={{
								width: cellW, height: cellH, display: "flex", alignItems: "center", justifyContent: "center",
								background: "rgba(40,40,40,1)",border: "1px solid rgba(255,255,255,0.15)",
								fontSize: "2em", color: "white", userSelect: "none", cursor: "pointer",
							}}
							onClick={()=>RunInAction_Set(()=>uiState.overlay = OverlayPanel.none)}
						>
							✕
						</div>
					);
				}
				if (i == cellIndex_toggle) {
					return (
						<div key={i}
							style={ES(
								{
									width: cellW, height: cellH, display: "flex", alignItems: "center", justifyContent: "center",
									background: "rgba(40,40,40,1)", border: "1px solid rgba(255,255,255,0.15)",
									fontSize: "2em", color: "white", userSelect: "none", cursor: "pointer",
								},
								showTitles && {fontWeight: "bold"},
							)}
							onClick={()=>{
								setShowTitles(!showTitles);
							}}
						>
							o
						</div>
					);
				}

				const dreamCellIndex = getDreamCellIndex(i);
				const letter = dreamCellLetters[dreamCellIndex];
				return <RecallGridCell key={i} letter={letter} showTitles={showTitles}/>;
			})}
		</div>
	);
});

const RecallGridCell = observer_mgl((props: {letter: string, showTitles: boolean})=>{
	const {letter, showTitles} = props;

	const {anchorsMap_all, anchorsList_validIDs} = GetAllLetterAnchorEntities();
	const entity = anchorsMap_all.get(letter);
	const letterAnchorScenes = GetLastXDreamScenesPerLetterAnchor(1);
	const latestSceneOnGrid = [...letterAnchorScenes.values()].flatMap(a=>a).OrderBy(a=>GetStandardDeepOrderingKeyForDreamScene(a)).LastOrX();
	const latestSceneOnGrid_startTime = latestSceneOnGrid ? EstimateSegmentStartTime(latestSceneOnGrid.journalEntry, latestSceneOnGrid.segment) : null;

	const sceneForCell = letterAnchorScenes.get(letter)?.[0];
	const sceneForCell_startTime = sceneForCell ? EstimateSegmentStartTime(sceneForCell.journalEntry, sceneForCell.segment) : null;
	const sceneForCell_within12HrOfLatest = latestSceneOnGrid_startTime && sceneForCell_startTime
		&& Math.abs(latestSceneOnGrid_startTime - sceneForCell_startTime) <= 12 * hourInMS;

	const imgSrc = entity?.image_embedded || entity?.url || null;

	return (
		<div style={ES(
			{
				width: cellW, height: cellH, position: "relative", overflow: "hidden",
				background: "rgba(20,20,20,1)",
			},
			{border: sceneForCell_within12HrOfLatest
				? "3px solid rgba(255,255,255,0.8)"
				: "1px solid rgba(255,255,255,0.1)"},
		)}>
			{imgSrc && <img src={imgSrc} style={{
				position: "absolute", inset: 0,
				width: "100%", height: "100%",
				objectFit: "cover",
				objectPosition: "top",
			}}/>}
			<div style={{
				position: "absolute", left: 0, top: 0,
				fontSize: 16, lineHeight: "12px",
				fontWeight: "bold", color: "rgba(255,255,255,1)",
				textShadow: "rgb(10, 10, 10) -1px 0px, rgb(10, 10, 10) 0px 1px, rgb(10, 10, 10) 1px 0px, rgb(10, 10, 10) 0px -1px",
			}}>{letter.toUpperCase()}</div>
			{showTitles &&
			<div style={{
				position: "absolute", bottom: 0,
				fontSize: 16, lineHeight: "12px",
				fontWeight: "bold", color: "rgba(255,255,255,1)",
				textShadow: "rgb(10, 10, 10) -1px 0px, rgb(10, 10, 10) 0px 1px, rgb(10, 10, 10) 1px 0px, rgb(10, 10, 10) 0px -1px",
			}}>{sceneForCell?.sceneTitle}</div>}
		</div>
	);
});